import { useEffect, useMemo, useRef, useState } from "react";
import "./NewOrderV2.css";

const tabs = [
  { key: "order", label: "Order", title: "Order", desc: "Fast order number, date, customer details and inline customer suggestions." },
  { key: "power", label: "Power", title: "Power", desc: "Compact power punching. Addition stays intentional and separate." },
  { key: "products", label: "Products", title: "Products", desc: "Add frames, lenses, sunglasses, contact lenses and accessories first." },
  { key: "pairing", label: "Pairing", title: "Pairing", desc: "Connect frame plus lens, or keep products standalone." },
  { key: "notes", label: "Notes", title: "Notes", desc: "Internal notes first, customer-visible note last." },
  { key: "payment", label: "Payment", title: "Payment", desc: "Advance payment for the new order being created." },
  { key: "review", label: "Review", title: "Review", desc: "Final compact check before the real save flow is connected later." },
  { key: "test", label: "Test", title: "Team Test", desc: "Quick scenarios and visual checks for the order slip." }
];

const sampleCustomers = [
  { id: "sample-customer-1", source: "sample", name: "Amit Sharma", mobile: "9810012345", lastOrder: "GC-20260621-004 · Progressive lens" },
  { id: "sample-customer-2", source: "sample", name: "Ritu Verma", mobile: "9876543210", lastOrder: "GC-20260618-012 · Frame + lens" },
  { id: "sample-customer-3", source: "sample", name: "Sumit Gupta", mobile: "9999011111", lastOrder: "GC-20260610-008 · Contact lens" }
];

const noteTabs = [
  ["internal", "Internal"],
  ["product", "Product/Fitting"],
  ["delivery", "Delivery"],
  ["print", "Print/WhatsApp"],
  ["customer", "Customer"]
];

const productLabels = {
  frame: ["Frame", "Frame only or pair later"],
  lens: ["Lens", "Lens only or attach to frame"],
  sunglass: ["Sunglass", "Plain or power sunglass"],
  contact_lens: ["Contact Lens", "Uses CL power/manual power"],
  solution: ["Solution", "Retail item"],
  accessory: ["Accessory", "Case, cloth, repair, etc."]
};

const initialProducts = [];

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  }).format(Number(value || 0));

function orderTotal(order) {
  return Number(order?.payment?.totalAmount || order?.totalAmount || 0);
}

function orderCollected(order) {
  const payment = order?.payment || {};
  const collectionRows = payment.collections || payment.paymentHistory || [];
  const laterCollection = collectionRows.reduce((sum, row) => sum + Number(row.amount || row.amountReceived || 0), 0);
  return Number(payment.advanceReceived || payment.advance || payment.paidAmount || 0) + laterCollection;
}

function orderBalanceDue(order) {
  const total = orderTotal(order);
  if (total > 0) return Math.max(0, total - orderCollected(order));
  return Math.max(0, Number(order?.payment?.balance || 0));
}

function orderPaidStatus(order) {
  return orderBalanceDue(order) <= 0 ? "Paid" : order?.payment?.status || "Pending";
}

function orderDelivered(order) {
  return /delivered/i.test(String(order?.orderDetails?.deliveryStatus || order?.deliveryStatus || ""));
}

function orderCustomerLabel(order) {
  return order?.customer?.name || order?.customerName || "Walk-in customer";
}

function localDateTimeValue(date = new Date()) {
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function initials(name) {
  return String(name || "C")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function matchCustomers(value, field, customers) {
  const needle = String(value || "").trim().toLowerCase();
  if (needle.length < 2) return [];
  return customers.filter((customer) => String(customer[field] || "").toLowerCase().includes(needle)).slice(0, 6);
}

function PowerControl({ label, defaultSign = "", defaultValue = "" }) {
  const [sign, setSign] = useState(defaultSign);
  const [value, setValue] = useState(defaultValue);

  return (
    <div className="nov2-compact-power">
      <label>{label}</label>
      <div className="nov2-power-control-line">
        <select value={sign} onChange={(event) => setSign(event.target.value)} aria-label={`${label} sign`}>
          <option value="">Sign</option>
          <option value="-">-</option>
          <option value="+">+</option>
        </select>
        <select value={value} onChange={(event) => setValue(event.target.value)} aria-label={`${label} value`}>
          {["", "0.00", "0.25", "0.50", "0.75", "1.00", "1.25", "1.50", "1.75", "2.00", "2.25", "2.50", "2.75", "3.00", "3.50", "4.00"].map((option) => (
            <option key={option} value={option}>
              {option || "Value"}
            </option>
          ))}
        </select>
      </div>
      <span>{value ? `${sign || ""}${value}` : "-"}</span>
    </div>
  );
}

function AxisControl({ label = "AXIS", defaultValue = "" }) {
  return (
    <div className="nov2-field">
      <label>{label}</label>
      <input defaultValue={defaultValue} inputMode="numeric" placeholder="Axis" />
    </div>
  );
}

export default function NewOrderV2({ customers = [], orders = [], onSaveOrder, onMarkDelivered }) {
  const rootRef = useRef(null);
  const [active, setActive] = useState("order");
  const [activeNote, setActiveNote] = useState("internal");
  const [notes, setNotes] = useState({
    internal: "",
    product: "",
    delivery: "",
    print: "",
    customer: ""
  });
  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [activeLookup, setActiveLookup] = useState(null);
  const [products, setProducts] = useState(initialProducts);
  const [discountType, setDiscountType] = useState("amount");
  const [discountValue, setDiscountValue] = useState(0);
  const [payments, setPayments] = useState([
    { id: "pay-1", mode: "", amount: "", ref: "", receivedAt: localDateTimeValue() }
  ]);
  const [orderAction, setOrderAction] = useState({ status: "idle", message: "" });
  const [toast, setToast] = useState("");
  const [saveState, setSaveState] = useState({ status: "idle", message: "", orderNumber: "" });

  const activeTab = tabs.find((tab) => tab.key === active) || tabs[0];
  const customerOptions = useMemo(() => {
    const realCustomers = (customers || []).map((customer) => ({
      id: customer.id,
      source: "real",
      name: customer.name || customer.customerName || "Customer",
      mobile: customer.phone || customer.mobile || "",
      lastOrder: customer.walkInId || customer.lastOrder || "Existing customer"
    }));
    const seen = new Set();
    return [...realCustomers, ...sampleCustomers].filter((customer) => {
      const key = `${customer.source}-${customer.id}-${customer.mobile}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [customers]);
  const nameMatches = activeLookup === "name" ? matchCustomers(customerName, "name", customerOptions) : [];
  const mobileMatches = activeLookup === "mobile" ? matchCustomers(customerMobile, "mobile", customerOptions) : [];
  const subtotal = products.reduce((sum, product) => sum + Number(product.amount || 0), 0);
  const discount = discountType === "percent" ? Math.round((subtotal * Number(discountValue || 0)) / 100) : Number(discountValue || 0);
  const netTotal = Math.max(0, subtotal - discount);
  const paidTotal = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const balance = Math.max(0, netTotal - paidTotal);
  const paymentStatus = balance === 0 && netTotal > 0 ? "Paid" : paidTotal > 0 ? "Part paid" : "Unpaid";

  const pairingText = useMemo(() => {
    const frame = products.find((product) => product.type === "frame" || product.type === "sunglass");
    const lens = products.find((product) => product.type === "lens");
    if (frame && lens) return `${lens.name} paired with ${frame.name}`;
    if (lens) return `${lens.name} marked for customer-own frame`;
    return "Add frame and lens to preview pairing.";
  }, [products]);
  const sortedOrders = useMemo(
    () => [...(orders || [])].sort((a, b) => new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0)),
    [orders]
  );
  const recentOrders = useMemo(() => sortedOrders.slice(0, 12), [sortedOrders]);

  useEffect(() => {
    function closeLookupOnOutsideClick(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setActiveLookup(null);
      }
    }

    document.addEventListener("pointerdown", closeLookupOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeLookupOnOutsideClick);
  }, []);

  function showToast(message) {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  }

  function selectCustomer(customer) {
    setCustomerName(customer.name);
    setCustomerMobile(customer.mobile);
    setSelectedCustomer(customer);
    setActiveLookup(null);
    showToast("Customer filled.");
  }

  function addProduct(type) {
    const [label] = productLabels[type];
    setProducts((current) => [
      ...current,
      {
        id: `preview-${type}-${current.length + 1}`,
        type,
        name: `${label} item`,
        amount: type === "lens" ? 2500 : type === "frame" ? 1500 : 500,
        linkedTo: "standalone",
        source: "from_store",
        note: ""
      }
    ]);
  }

  function updateProduct(id, changes) {
    setProducts((current) => current.map((product) => (product.id === id ? { ...product, ...changes } : product)));
  }

  function removeProduct(id) {
    setProducts((current) => current.filter((product) => product.id !== id));
  }

  async function saveOrder() {
    if (!onSaveOrder || saveState.status === "saving") return;
    setSaveState({ status: "saving", message: "Saving order...", orderNumber: "" });
    try {
      const saved = await onSaveOrder({
        customerName,
        customerMobile,
        selectedCustomerId: selectedCustomer?.source === "real" ? selectedCustomer.id : "",
        products,
        subtotal,
        discountType,
        discountValue,
        discount,
        netTotal,
        payments,
        paidTotal,
        balance,
        notes
      });
      setSaveState({ status: "saved", message: `${saved?.orderNumber || "Order"} saved online.`, orderNumber: saved?.orderNumber || "" });
      showToast(`${saved?.orderNumber || "Order"} saved.`);
      setActive("review");
    } catch (error) {
      const message = error?.message || "Order could not be saved.";
      setSaveState({ status: "error", message, orderNumber: "" });
      showToast("Save failed. Check message.");
    }
  }

  function addPayment() {
    setPayments((current) => [...current, { id: `pay-${current.length + 1}`, mode: "", amount: "", ref: "", receivedAt: localDateTimeValue() }]);
  }

  function updatePayment(id, field, value) {
    setPayments((current) => current.map((payment) => (payment.id === id ? { ...payment, [field]: field === "amount" ? Number(value || 0) : value } : payment)));
  }

  function removePayment(id) {
    setPayments((current) => current.filter((payment) => payment.id !== id));
  }

  async function markDelivered(order) {
    if (!onMarkDelivered || orderAction.status === "saving") return;
    setOrderAction({ status: "saving", message: "Saving delivery..." });
    try {
      const saved = await onMarkDelivered(order);
      setOrderAction({ status: "saved", message: `${saved?.orderNumber || "Order"} delivered.` });
      showToast("Order delivered.");
    } catch (error) {
      const message = error?.message || "Delivery could not be saved.";
      setOrderAction({ status: "error", message });
      showToast("Delivery failed.");
    }
  }

  return (
    <div className="new-order-v2" ref={rootRef}>
      <div className="nov2-app">
        <header className="nov2-header">
          <div className="nov2-head-row">
            <div className="nov2-brand">
              <div className="nov2-mark">SC</div>
              <div>
                <strong>Fast Order Slip V13</strong>
                <span>New order, balance collection and delivery</span>
              </div>
            </div>
            <button className="nov2-save-btn" type="button" disabled={saveState.status === "saving"} onClick={saveOrder}>
              {saveState.status === "saving" ? "Saving..." : saveState.status === "saved" ? "Saved ✓" : "Save Order"}
            </button>
          </div>
          <nav className="nov2-tabs" aria-label="Order Slip tabs">
            {tabs.map((tab) => (
              <button key={tab.key} className={active === tab.key ? "active" : ""} type="button" onClick={() => setActive(tab.key)}>
                {tab.label}
              </button>
            ))}
          </nav>
        </header>

        <main className="nov2-page">
          <section className="nov2-main">
            <div className="nov2-hero">
              <h1>{activeTab.title}</h1>
              <p>{activeTab.desc}</p>
            </div>

            {active === "order" ? (
              <>
                <div className="nov2-card">
                  <div className="nov2-card-head">
                    <div>
                      <h2>Order basics</h2>
                      <p>Auto-filled but manually editable.</p>
                    </div>
                  <span className="nov2-tag">{saveState.orderNumber || "Fast"}</span>
                  </div>
                  <div className="nov2-grid two">
                    <div className="nov2-field">
                      <label>Order number</label>
                      <input placeholder="Auto after save" />
                    </div>
                    <div className="nov2-field">
                      <label>Order date</label>
                      <input type="date" />
                    </div>
                  </div>
                </div>

                <div className="nov2-card">
                  <div className="nov2-card-head">
                    <div>
                      <h2>Customer</h2>
                      <p>Type inside Phone or Name. Matching customers appear below the same field.</p>
                    </div>
                    <span className="nov2-tag">Inbuilt suggestions</span>
                  </div>
                  <div className="nov2-grid two">
                    <div className="nov2-field">
                      <label>Phone</label>
                      <input
                        value={customerMobile}
                        onChange={(event) => {
                          setCustomerMobile(event.target.value);
                          setSelectedCustomer(null);
                          setActiveLookup("mobile");
                        }}
                        onFocus={() => setActiveLookup("mobile")}
                        placeholder="Type mobile number"
                        inputMode="tel"
                        autoComplete="off"
                      />
                      {mobileMatches.length ? <SuggestionList matches={mobileMatches} onPick={selectCustomer} /> : activeLookup === "mobile" && customerMobile.length > 1 ? <span className="nov2-empty">Keep typing for a new customer.</span> : null}
                    </div>
                    <div className="nov2-field">
                      <label>Name</label>
                      <input
                        value={customerName}
                        onChange={(event) => {
                          setCustomerName(event.target.value);
                          setSelectedCustomer(null);
                          setActiveLookup("name");
                        }}
                        onFocus={() => setActiveLookup("name")}
                        placeholder="Type customer name"
                        autoComplete="off"
                      />
                      {nameMatches.length ? <SuggestionList matches={nameMatches} onPick={selectCustomer} /> : activeLookup === "name" && customerName.length > 1 ? <span className="nov2-empty">Keep typing for a new customer.</span> : null}
                    </div>
                    <div className="nov2-field">
                      <label>Store</label>
                      <select defaultValue="">
                        <option value="">Select store</option>
                        <option>Framesbay CO</option>
                        <option>Noida 120</option>
                        <option>Noida 137</option>
                        <option>Crossing Republik</option>
                      </select>
                    </div>
                    <div className="nov2-field">
                      <label>Staff</label>
                      <select defaultValue="">
                        <option value="">Select staff</option>
                        <option>Super Admin</option>
                        <option>Store Manager</option>
                        <option>Optometrist</option>
                      </select>
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            {active === "power" ? (
              <div className="nov2-card">
                <div className="nov2-card-head">
                  <div>
                    <h2>Power</h2>
                    <p>Compact power punching. Addition is same for both eyes by default.</p>
                  </div>
                  <span className="nov2-tag">Compact</span>
                </div>
                <EyePower title="Right Eye / OD" side="right" />
                <EyePower title="Left Eye / OS" side="left" />
                <div className="nov2-addition-card">
                  <div className="nov2-section-title">Addition</div>
                  <label className="nov2-check-line">
                    <input className="nov2-big-check" type="checkbox" />
                    <span>Same ADD for both eyes</span>
                  </label>
                  <div className="nov2-add-grid">
                    <PowerControl label="Right ADD" />
                    <PowerControl label="Left ADD" />
                  </div>
                </div>
                <div className="nov2-card flat">
                  <div className="nov2-card-head">
                    <div>
                      <h2>Contact Lens Power</h2>
                      <p>Different from spectacle power. Convert suggested power or enter manually.</p>
                    </div>
                    <span className="nov2-tag">Separate</span>
                  </div>
                  <div className="nov2-action-row">
                    <button className="nov2-btn soft" type="button" onClick={() => showToast("Converted CL preview only.")}>
                      Convert to CL
                    </button>
                    <button className="nov2-btn secondary" type="button">
                      Manual CL
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {active === "products" ? (
              <>
                <div className="nov2-card">
                  <div className="nov2-card-head">
                    <div>
                      <h2>Add Products</h2>
                      <p>Add products independently first. Pairing comes next.</p>
                    </div>
                    <span className="nov2-tag">Product-first</span>
                  </div>
                  <div className="nov2-product-palette">
                    {Object.entries(productLabels).map(([key, [label, desc]]) => (
                      <button key={key} type="button" onClick={() => addProduct(key)}>
                        {label}
                        <span>{desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="nov2-card warning">
                  <div className="nov2-card-head">
                    <div>
                      <h2>Product-first rule</h2>
                      <p>Handles frame only, lens only, multiple frames, multiple lenses, and power sunglasses.</p>
                    </div>
                    <span className="nov2-tag warn">Important</span>
                  </div>
                </div>
                {!products.length ? (
                  <div className="nov2-empty-products">
                    No products added yet. Tap Frame, Lens, Contact Lens, Solution or Accessory above to add the first item.
                  </div>
                ) : null}
                {products.map((product, index) => (
                  <ProductLine
                    key={product.id}
                    product={product}
                    index={index}
                    onChange={(changes) => updateProduct(product.id, changes)}
                    onRemove={() => removeProduct(product.id)}
                  />
                ))}
              </>
            ) : null}

            {active === "pairing" ? (
              <>
                <div className="nov2-card">
                  <div className="nov2-card-head">
                    <div>
                      <h2>Pairing / Use</h2>
                      <p>Decide which lens goes with which frame/sunglass/customer-own-frame.</p>
                    </div>
                    <span className="nov2-tag">Smart linking</span>
                  </div>
                  <div className="nov2-action-row">
                    <button className="nov2-btn soft" type="button" onClick={() => showToast("Pairing suggestion applied in preview.")}>
                      Auto suggest pairing
                    </button>
                    <button className="nov2-btn secondary" type="button">
                      Refresh
                    </button>
                  </div>
                </div>
                <div className="nov2-assignment-card">
                  <div className="nov2-line-head">
                    <strong>Lens assignment</strong>
                    <span className="nov2-tag ok">Suggested</span>
                  </div>
                  <p className="nov2-mini-note">{pairingText}</p>
                  <div className="nov2-relationship-box">
                    <div className="nov2-grid two">
                      <div className="nov2-field">
                        <label>Frame / Sunglass</label>
                        <select defaultValue="">
                          <option value="">Select frame / sunglass</option>
                          {products.filter((product) => product.type === "frame" || product.type === "sunglass").map((product) => (
                            <option key={product.id} value={product.id}>{product.name}</option>
                          ))}
                          <option value="customer-own-frame">Customer own frame</option>
                        </select>
                      </div>
                      <div className="nov2-field">
                        <label>Lens / Power source</label>
                        <select defaultValue="">
                          <option value="">Select lens / power source</option>
                          {products.filter((product) => product.type === "lens").map((product) => (
                            <option key={product.id} value={product.id}>{product.name}</option>
                          ))}
                          <option value="master-power">Master spectacle power</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            {active === "notes" ? (
              <div className="nov2-card">
                <div className="nov2-card-head">
                  <div>
                    <h2>Notes</h2>
                    <p>Separate notes tab so staff does not overload product fields.</p>
                  </div>
                  <span className="nov2-tag">Separate tab</span>
                </div>
                <div className="nov2-note-tabs">
                  {noteTabs.map(([key, label]) => (
                    <button key={key} className={activeNote === key ? "active" : ""} type="button" onClick={() => setActiveNote(key)}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="nov2-field">
                  <label>{noteTabs.find(([key]) => key === activeNote)?.[1]} note</label>
                  <textarea
                    value={notes[activeNote] || ""}
                    onChange={(event) => setNotes((current) => ({ ...current, [activeNote]: event.target.value }))}
                    placeholder={activeNote === "customer" ? "Customer visible note..." : "Staff-only note, fitting instruction, delivery promise, or print message..."}
                  />
                </div>
              </div>
            ) : null}

            {active === "payment" ? (
              <div className="nov2-card">
                <div className="nov2-card-head">
                  <div>
                    <h2>Payment</h2>
                    <p>Take advance for this order, or collect balance from an already saved order.</p>
                  </div>
                  <span className="nov2-tag">Split payment</span>
                </div>
                <MoneyRow subtotal={subtotal} discount={discount} netTotal={netTotal} />
                <div className="nov2-discount-box">
                  <div className="nov2-grid three">
                    <div className="nov2-field">
                      <label>Discount type</label>
                      <select value={discountType} onChange={(event) => setDiscountType(event.target.value)}>
                        <option value="amount">₹ Amount</option>
                        <option value="percent">% Percent</option>
                      </select>
                    </div>
                    <div className="nov2-field">
                      <label>Discount value</label>
                      <input type="number" value={discountValue} onChange={(event) => setDiscountValue(event.target.value)} />
                    </div>
                    <div className="nov2-field">
                      <label>Discount reason</label>
                      <input placeholder="Offer / staff approval / coupon" />
                    </div>
                  </div>
                </div>
                <div className="nov2-money-row">
                  <MoneyBox label="Paid" value={money(paidTotal)} />
                  <MoneyBox label="Balance" value={money(balance)} />
                  <MoneyBox label="Status" value={paymentStatus} />
                </div>
                <div className="nov2-card flat">
                  <div className="nov2-card-head">
                    <div>
                      <h2>Advance / Payment For This New Order</h2>
                      <p>Add UPI + Card + Cash while creating this order.</p>
                    </div>
                    <span className="nov2-tag ok">Visible</span>
                  </div>
                  {payments.map((payment, index) => (
                    <div className="nov2-payment-row" key={payment.id}>
                      <div className="nov2-payment-row-title">
                        <strong>Payment {index + 1}</strong>
                        <button className="nov2-btn danger" type="button" onClick={() => removePayment(payment.id)}>
                          Remove
                        </button>
                      </div>
                      <div className="nov2-grid three">
                        <div className="nov2-field">
                          <label>Amount</label>
                          <input type="number" value={payment.amount} onChange={(event) => updatePayment(payment.id, "amount", event.target.value)} />
                        </div>
                        <div className="nov2-field">
                          <label>Mode</label>
                          <select value={payment.mode} onChange={(event) => updatePayment(payment.id, "mode", event.target.value)}>
                            <option value="">Select mode</option>
                            {["Cash", "UPI", "Card", "Paytm", "Bank Transfer", "Other"].map((mode) => (
                              <option key={mode}>{mode}</option>
                            ))}
                          </select>
                        </div>
                        <div className="nov2-field">
                          <label>Reference</label>
                          <input value={payment.ref} onChange={(event) => updatePayment(payment.id, "ref", event.target.value)} placeholder="Optional" />
                        </div>
                        <div className="nov2-field">
                          <label>Received date/time</label>
                          <input type="datetime-local" value={payment.receivedAt || ""} onChange={(event) => updatePayment(payment.id, "receivedAt", event.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button className="nov2-btn ghost" type="button" onClick={addPayment}>
                    + Add another payment mode
                  </button>
                </div>
              </div>
            ) : null}

            {active === "review" ? (
              <div className="nov2-card">
                <div className="nov2-card-head">
                  <div>
                    <h2>Review</h2>
                    <p>Final fast check before saving or sharing Order Slip.</p>
                  </div>
                  <span className="nov2-tag ok">Ready</span>
                </div>
                <ReviewBlock title="Customer" text={`${customerName || "Walk-in customer"} · ${customerMobile || "Phone optional"}${selectedCustomer ? " · selected from suggestions" : ""}`} />
                <ReviewBlock title="Products" text={`${products.length} products · ${money(subtotal)}`} />
                <ReviewBlock title="Pairing" text={pairingText} />
                <ReviewBlock title="Payment" text={`Net ${money(netTotal)} · Paid ${money(paidTotal)} · Balance ${money(balance)} · ${paymentStatus}`} />
                {saveState.message ? <ReviewBlock title={saveState.status === "error" ? "Save error" : "Save status"} text={saveState.message} /> : null}
                {orderAction.message ? <ReviewBlock title={orderAction.status === "error" ? "Action error" : "Order action"} text={orderAction.message} /> : null}
                <div className="nov2-history">
                  <h3>Recent order history</h3>
                  {recentOrders.length ? recentOrders.map((order) => {
                    const due = orderBalanceDue(order);
                    const delivered = orderDelivered(order);
                    return (
                    <div className="nov2-history-card" key={order.id || order.orderNumber}>
                      <div>
                        <strong>{order.orderNumber || "Order"}</strong>
                        <span>{orderCustomerLabel(order)} · Total {money(orderTotal(order))} · Balance {money(due)}</span>
                        <small>{order.date || ""} · {orderPaidStatus(order)} · {delivered ? "Delivered" : "Not delivered"}</small>
                      </div>
                      <div className="nov2-history-actions">
                        <button type="button" onClick={() => showToast(`${order.orderNumber || "Order"} selected.`)}>View</button>
                        <button type="button" onClick={() => showToast("Edit will stay in the existing editable order flow.")}>Edit</button>
                        {!delivered ? <button type="button" onClick={() => markDelivered(order)}>Mark Delivered</button> : null}
                      </div>
                    </div>
                    );
                  }) : (
                    <div className="nov2-empty-products">No saved orders yet. Save an order first, then pending balance and delivery actions will appear here.</div>
                  )}
                </div>
              </div>
            ) : null}

            {active === "test" ? (
              <div className="nov2-card">
                <div className="nov2-card-head">
                  <div>
                    <h2>Team Test Mode</h2>
                    <p>Use this screen only for visual testing before touching real save logic.</p>
                  </div>
                  <span className="nov2-tag ok">Testable</span>
                </div>
                <div className="nov2-action-row">
                  <button className="nov2-btn soft" type="button" onClick={() => setActive("products")}>Frame only</button>
                  <button className="nov2-btn soft" type="button" onClick={() => setActive("products")}>Lens only</button>
                  <button className="nov2-btn soft" type="button" onClick={() => setActive("products")}>Power Sunglass</button>
                </div>
                <div className="nov2-checklist">
                  {["Mobile tabs remain visible", "No horizontal overflow", "Inline customer suggestions work", "Power entry is compact", "Products can be added independently", "Pairing is clear", "Notes order is safe", "History has clear Edit button"].map((item) => (
                    <label className="nov2-check-item" key={item}>
                      <input type="checkbox" />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
          </section>

          <aside className="nov2-desktop-summary">
            <div className="nov2-summary-card">
              <h2>Slip summary</h2>
              <SummaryLine label="Order No." value={saveState.orderNumber || "Auto after save"} />
              <SummaryLine label="Customer" value={customerName || "Walk-in"} />
              <SummaryLine label="Products" value={products.length} />
              <SummaryLine label="Net Total" value={money(netTotal)} />
              <SummaryLine label="Balance" value={money(balance)} />
              <SummaryLine label="Status" value={paymentStatus} />
            </div>
          </aside>
        </main>

        <div className="nov2-bottom-bar">
          <div className="nov2-bottom-summary">
            <div>
              <span>Balance</span>
              <strong>{money(balance)}</strong>
            </div>
            <div>
              <span>Tab</span>
              <strong>{activeTab.label}</strong>
            </div>
          </div>
          <div className="nov2-bottom-actions">
            <button className="nov2-btn secondary" type="button" onClick={() => setActive(tabs[Math.max(0, tabs.findIndex((tab) => tab.key === active) - 1)].key)}>
              Back
            </button>
            <button className="nov2-btn" type="button" onClick={() => {
              const currentIndex = tabs.findIndex((tab) => tab.key === active);
              if (currentIndex >= tabs.length - 1) saveOrder();
              else setActive(tabs[currentIndex + 1].key);
            }} disabled={saveState.status === "saving"}>
              {saveState.status === "saving" ? "Saving..." : active === "test" ? "Save Order" : "Next"}
            </button>
          </div>
        </div>

        {saveState.status === "error" ? <div className="nov2-error">{saveState.message}</div> : null}
        {toast ? <div className="nov2-toast">{toast}</div> : null}
      </div>
    </div>
  );
}

function SuggestionList({ matches, onPick }) {
  return (
    <div className="nov2-suggestions">
      {matches.map((customer) => (
        <button key={customer.id} type="button" onClick={() => onPick(customer)}>
          <span className="nov2-lookup-initials">{initials(customer.name)}</span>
          <span>
            <strong>{customer.name}</strong>
            <small>{customer.mobile} · {customer.lastOrder}</small>
          </span>
          <em>Select</em>
        </button>
      ))}
    </div>
  );
}

function EyePower({ title, side }) {
  return (
    <div className="nov2-eye-power-card">
      <div className="nov2-eye-power-head">
        <strong>{title}</strong>
        <button className="nov2-btn soft" type="button">Copy {side === "right" ? "to Left" : "to Right"}</button>
      </div>
      <div className="nov2-compact-rx-grid">
        <PowerControl label="SPH" />
        <PowerControl label="CYL" />
        <AxisControl />
        <div className="nov2-field">
          <label>PD</label>
          <input inputMode="decimal" placeholder="PD" />
        </div>
      </div>
    </div>
  );
}

function ProductLine({ product, index, onChange, onRemove }) {
  const [label, desc] = productLabels[product.type] || ["Product", "Order item"];
  return (
    <div className="nov2-product-line">
      <div className="nov2-line-head">
        <div>
          <strong>Product {index + 1}: {label}</strong>
          <p className="nov2-mini-note">{desc}</p>
        </div>
        <span className="nov2-tag">{money(product.amount)}</span>
      </div>
      <div className="nov2-product-actions">
        <button className="nov2-btn danger" type="button" onClick={onRemove}>
          Remove product
        </button>
      </div>
      <div className="nov2-grid two">
        <div className="nov2-field">
          <label>Product details</label>
          <input value={product.name || ""} onChange={(event) => onChange({ name: event.target.value })} />
        </div>
        <div className="nov2-field">
          <label>Amount</label>
          <input value={product.amount || ""} onChange={(event) => onChange({ amount: Number(event.target.value || 0) })} inputMode="numeric" />
        </div>
        <div className="nov2-field">
          <label>Source</label>
          <select value={product.source || "store"} onChange={(event) => onChange({ source: event.target.value })}>
            <option value="from_store">From store</option>
            <option value="customer_own">Customer own</option>
            <option value="awaited">Awaited</option>
          </select>
        </div>
        <div className="nov2-field">
          <label>Quick note</label>
          <input value={product.note || ""} onChange={(event) => onChange({ note: event.target.value })} />
        </div>
      </div>
    </div>
  );
}

function MoneyRow({ subtotal, discount, netTotal }) {
  return (
    <div className="nov2-money-row">
      <MoneyBox label="Subtotal" value={money(subtotal)} />
      <MoneyBox label="Discount" value={money(discount)} />
      <MoneyBox label="Net Total" value={money(netTotal)} />
    </div>
  );
}

function MoneyBox({ label, value }) {
  return (
    <div className="nov2-money-box">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ReviewBlock({ title, text }) {
  return (
    <div className="nov2-review-block">
      <strong>{title}</strong>
      <p className="nov2-mini-note">{text}</p>
    </div>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div className="nov2-summary-line">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
